// Wave 9 — AI image generator dialog (Nano Banana / Runway).
//
// Shared between the Media Library "Generate with AI" entry point and the
// Post Composer's media section. The dialog owns the prompt + provider +
// aspect-ratio state, calls the gateway, and either:
//   - persists the image as a hosted social_media_asset (default), and/or
//   - returns the saved asset rows back to the caller via onSaved so the
//     caller can attach them to a draft (composer flow).
//
// The dialog hides itself entirely when the workspace's master AI gate or
// image gate is off, so the entry-point button can render a hint.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Sparkles, ImageIcon, RefreshCcw, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SocialMediaAsset } from "@shared/schema";

type ProviderKey = "nano_banana" | "runway";
type AspectRatio = "1:1" | "4:5" | "9:16" | "16:9";

type ImagePolicyResponse = {
  policy: {
    imageEnabled: boolean;
    nanoBananaEnabled: boolean;
    runwayEnabled: boolean;
    maxPerUserPerDay: number | null;
  };
  usedToday: number;
  remainingToday: number | null;
  costCents: Record<ProviderKey, number>;
  aspectRatios: AspectRatio[];
  aiEnabled: boolean;
};

type GenerateResponse = {
  asset: SocialMediaAsset | null;
  dataUrl?: string;
  provider: ProviderKey;
  modelKey: string;
  costCents: number;
  aspectRatio: AspectRatio;
  prompt: string;
};

const PROVIDER_LABELS: Record<ProviderKey, { name: string; tagline: string }> = {
  nano_banana: {
    name: "Nano Banana",
    tagline: "Fast, photo-realistic. Great for social thumbnails.",
  },
  runway: {
    name: "Runway Gen-4",
    tagline: "Stylized, cinematic. Slower but more dramatic.",
  },
};

const ASPECT_HINT: Record<AspectRatio, string> = {
  "1:1": "Instagram square",
  "4:5": "IG portrait",
  "9:16": "Story / Reel",
  "16:9": "FB / X landscape",
};

function fmtCents(c: number): string {
  return c < 100 ? `${c}¢` : `$${(c / 100).toFixed(2)}`;
}

interface AIImageGeneratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called once per "Save" press, with the just-persisted asset row. Used by
  // the composer to attach it to the current draft. The library doesn't need
  // it (a query invalidation does the work).
  onSaved?: (asset: SocialMediaAsset) => void;
  // Initial prompt to seed the textarea (e.g. populated from the post caption).
  initialPrompt?: string;
  // Tells the dialog this was opened from the composer so we can tweak copy
  // ("Save & attach" instead of "Save to library").
  attachMode?: boolean;
}

export function AIImageGeneratorDialog({
  open,
  onOpenChange,
  onSaved,
  initialPrompt,
  attachMode,
}: AIImageGeneratorDialogProps) {
  const { toast } = useToast();
  const policyQ = useQuery<ImagePolicyResponse>({
    queryKey: ["/api/social/ai-images/policy"],
    enabled: open,
  });

  // Local state.
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState<ProviderKey>("nano_banana");
  const [aspect, setAspect] = useState<AspectRatio>("1:1");
  // Latest preview returned by the generate (preview-mode) call.
  const [preview, setPreview] = useState<GenerateResponse | null>(null);

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt?.slice(0, 4000) || "");
      setPreview(null);
      // If only one provider is enabled, default to it.
      const p = policyQ.data?.policy;
      if (p) {
        if (!p.nanoBananaEnabled && p.runwayEnabled) setProvider("runway");
        else setProvider("nano_banana");
      }
    }
  }, [open, initialPrompt, policyQ.data?.policy]);

  // Picks the cheapest available provider as the default once policy loads.
  const enabledProviders = useMemo<ProviderKey[]>(() => {
    const p = policyQ.data?.policy;
    if (!p) return [];
    const list: ProviderKey[] = [];
    if (p.nanoBananaEnabled) list.push("nano_banana");
    if (p.runwayEnabled) list.push("runway");
    return list;
  }, [policyQ.data?.policy]);

  // Generate (preview only — does not save).
  const generate = useMutation<GenerateResponse, any, void>({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/social/ai-images/generate", {
        provider,
        aspectRatio: aspect,
        prompt: prompt.trim(),
        save: false,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setPreview(data);
      // Invalidate balance + policy so quota counters update.
      queryClient.invalidateQueries({ queryKey: ["/api/ai/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/ai-images/policy"] });
    },
    onError: (err: any) => handleApiError(err, toast, "Could not generate image"),
  });

  // Save the previewed image. Hits a separate /save endpoint that takes the
  // already-generated data URL bytes — does NOT re-run the generator and
  // therefore does NOT charge a second AI credit. Storage quota is still
  // consumed since we're persisting the file.
  const save = useMutation<{ asset: SocialMediaAsset }, any, GenerateResponse>({
    mutationFn: async (toSave) => {
      const res = await apiRequest("POST", "/api/social/ai-images/save", {
        provider: toSave.provider,
        modelKey: toSave.modelKey,
        aspectRatio: toSave.aspectRatio,
        prompt: toSave.prompt,
        dataUrl: toSave.dataUrl,
      });
      return res.json();
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/media"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/storage/quota"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ai/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/ai-images/policy"] });
      if (resp.asset) {
        toast({
          title: attachMode ? "Saved & ready to attach" : "Saved to library",
          description: "Your AI image is in the Media library.",
        });
        onSaved?.(resp.asset);
      }
      // Reset preview so the user can generate another.
      setPreview(null);
    },
    onError: (err: any) => handleApiError(err, toast, "Could not save image"),
  });

  // Render — early returns for off / loading states.
  if (!open) return null;

  // While policy is loading we render a skeleton so the dialog opens cleanly.
  const loadingPolicy = policyQ.isLoading || !policyQ.data;
  const policy = policyQ.data;
  const featureOff =
    policy && (!policy.aiEnabled || !policy.policy.imageEnabled || enabledProviders.length === 0);
  const dailyCapHit =
    policy?.remainingToday != null && policy.remainingToday <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-ai-image-generator"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate an image with AI
          </DialogTitle>
          <DialogDescription>
            Describe what you want and pick a provider. We'll save the result to your media library.
          </DialogDescription>
        </DialogHeader>

        {loadingPolicy ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : featureOff ? (
          <div className="rounded-md border bg-muted/30 p-4 text-sm" data-testid="text-ai-image-feature-off">
            {!policy!.aiEnabled
              ? "AI is currently turned off for this workspace. Ask an admin to enable it."
              : !policy!.policy.imageEnabled
              ? "AI image generation is turned off for this workspace. Ask an admin to enable it under Admin → AI assist."
              : "No image providers are enabled. Ask an admin to enable Nano Banana or Runway."}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quota / cost strip. Always visible so the user sees the price. */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {policy!.remainingToday != null ? (
                <Badge
                  variant={dailyCapHit ? "destructive" : "secondary"}
                  data-testid="badge-ai-image-quota"
                >
                  {policy!.usedToday} / {policy!.policy.maxPerUserPerDay} used today
                </Badge>
              ) : (
                <Badge variant="secondary">No daily cap</Badge>
              )}
              <Badge variant="outline" data-testid="badge-ai-image-cost">
                {PROVIDER_LABELS[provider].name}: {fmtCents(policy!.costCents[provider])}/image
              </Badge>
            </div>

            {/* Provider picker. Only renders enabled providers. */}
            <div>
              <Label>Provider</Label>
              <RadioGroup
                value={provider}
                onValueChange={(v) => setProvider(v as ProviderKey)}
                className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2"
              >
                {enabledProviders.map((p) => (
                  <label
                    key={p}
                    htmlFor={`prov-${p}`}
                    className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer hover-elevate ${
                      provider === p ? "border-primary" : ""
                    }`}
                    data-testid={`label-provider-${p}`}
                  >
                    <RadioGroupItem
                      value={p}
                      id={`prov-${p}`}
                      data-testid={`radio-provider-${p}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{PROVIDER_LABELS[p].name}</div>
                      <div className="text-xs text-muted-foreground">
                        {PROVIDER_LABELS[p].tagline}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {fmtCents(policy!.costCents[p])} per image
                      </div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Aspect-ratio picker. */}
            <div>
              <Label>Aspect ratio</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {policy!.aspectRatios.map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant={aspect === r ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAspect(r)}
                    data-testid={`button-aspect-${r.replace(":", "-")}`}
                  >
                    <span className="font-mono">{r}</span>
                    <span className="ml-2 text-xs text-muted-foreground hidden sm:inline">
                      {ASPECT_HINT[r]}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Prompt textarea. */}
            <div>
              <Label htmlFor="ai-image-prompt">Prompt</Label>
              <Textarea
                id="ai-image-prompt"
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, 4000))}
                placeholder="A vibrant flat-lay of holiday catering platters on a wooden table, soft natural light, photorealistic"
                data-testid="input-ai-image-prompt"
              />
              <div className="text-xs text-muted-foreground mt-1">
                {prompt.length}/4000 characters
              </div>
            </div>

            {/* Preview. */}
            {preview && (
              <div className="rounded-md border p-3 space-y-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Preview ({preview.aspectRatio})
                </div>
                {preview.dataUrl && (
                  <img
                    src={preview.dataUrl}
                    alt={preview.prompt}
                    className="max-h-96 w-full object-contain rounded-md bg-muted"
                    data-testid="img-ai-image-preview"
                  />
                )}
                <div className="text-xs text-muted-foreground">
                  Generated with <span className="font-medium">{PROVIDER_LABELS[preview.provider].name}</span>
                  {" · "}
                  charged {fmtCents(preview.costCents)}.
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-ai-image"
          >
            Close
          </Button>

          {!featureOff && (
            <>
              {preview ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => generate.mutate()}
                  disabled={generate.isPending || !prompt.trim() || dailyCapHit}
                  data-testid="button-regenerate-ai-image"
                >
                  {generate.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 mr-1.5" />
                  )}
                  Try again ({fmtCents(policy!.costCents[provider])})
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => generate.mutate()}
                  disabled={generate.isPending || !prompt.trim() || dailyCapHit}
                  data-testid="button-generate-ai-image"
                >
                  {generate.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-1.5" />
                  )}
                  Generate ({fmtCents(policy!.costCents[provider])})
                </Button>
              )}
              {preview && (
                <Button
                  type="button"
                  onClick={() => preview && save.mutate(preview)}
                  disabled={save.isPending || dailyCapHit}
                  data-testid="button-save-ai-image"
                >
                  {save.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : attachMode ? (
                    <ImageIcon className="h-4 w-4 mr-1.5" />
                  ) : (
                    <Save className="h-4 w-4 mr-1.5" />
                  )}
                  {attachMode ? "Save & attach" : "Save to library"}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Centralized error parser. apiRequest throws errors whose `.message` is
// usually `"<status>: <json-body>"`. We pull the structured fields so users
// see the actionable copy ("Out of credits", "Daily cap reached", ...) rather
// than a raw HTTP code.
function handleApiError(err: any, toast: ReturnType<typeof useToast>["toast"], fallbackTitle: string) {
  let title = fallbackTitle;
  let description = err?.message || "Please try again.";
  try {
    const json = JSON.parse((err?.message || "").split(": ").slice(1).join(": "));
    if (json?.message) description = json.message;
    if (json?.code === "AI_BUDGET_EXCEEDED") {
      title = "Out of AI credits";
      description = "Ask your admin to raise the cap or buy more credits.";
    } else if (json?.code === "AI_USER_CAP") {
      title = "Daily cap reached";
    } else if (json?.code === "AI_IMAGE_DISABLED" || json?.code === "AI_PROVIDER_DISABLED") {
      title = "Disabled by admin";
    } else if (json?.code === "AI_DISABLED") {
      title = "AI is off";
    }
  } catch {}
  toast({ title, description, variant: "destructive" });
}
